/**
 * chroma.js — colour-key (chroma / luma key) matting for video-bg-remover.
 *
 * The SECOND engine, next to RVM. It exists because the two answer different
 * questions and the search demand for both is real (Wordstat, RU, broad match,
 * measured 2026-09-01): «удалить/убрать зелёный фон с видео» 413+267,
 * «хромакей онлайн» 563 with a ~1400 tail, «убрать чёрный фон с видео» 97+79.
 * None of it was served — the tool only ever had the neural path.
 *
 * WHY IT IS NOT A DOWNGRADE. On footage that really was shot against a screen,
 * a colour key is BETTER than the network, not worse: it is exact where the
 * network is approximate (single-pixel hair gaps, motion blur, semi-transparent
 * glass), it is per-pixel deterministic so it cannot flicker between frames,
 * and it costs no model download and no seconds-per-frame inference. The whole
 * clip keys at decode speed. RVM stays the answer for ordinary footage — a
 * person in a room — where there is no key colour at all.
 *
 * CONTRACT — identical to VbrRvmEngine.matte(): in an opaque source frame,
 * out an RGBA canvas whose RGB is the DESPILLED straight (non-premultiplied)
 * foreground and whose A is the matte. Everything downstream (VbrRoto's manual
 * corrections, VbrCompositor, VbrEncoder) therefore works unchanged, and the
 * rotoscoping editor can fix a colour key exactly as it fixes a neural one.
 *
 * KEY MODELS
 *   'color'  — distance in the CbCr chroma plane from a picked colour. Chroma,
 *              not RGB: a lit and a shadowed patch of the same green screen sit
 *              at the SAME point in CbCr and metres apart in RGB, which is why
 *              an RGB-distance key eats the shadow side of every real backdrop.
 *   'dark'   — luma key for black backgrounds (fireworks, sparks, smoke, light
 *              leaks — the overlay footage people actually buy packs of).
 *   'light'  — luma key for white backgrounds (product/whiteboard shots).
 *
 * SPILL. A green backdrop bounces green onto the subject. Suppression clamps
 * the key channel to the mean of the other two, mixed by `spill`. Done in the
 * SAME pass as the matte, because it must happen on the straight foreground
 * before anything composites it.
 *
 * Public API — window.VbrChroma:
 *   autoDetect(canvas)              → { mode, color:[r,g,b], confidence }
 *   matte(srcCanvas, opts)          → canvas   (RGBA: RGB=fg, A=alpha)
 *   defaults()                      → the opts shape with its defaults
 *
 *   opts: { mode:'color'|'dark'|'light', color:[r,g,b],
 *           tolerance:0..100, softness:0..100, spill:0..100 }
 */
(function (root) {
    'use strict';

    /* ── colour helpers ───────────────────────────────────────────────────── */

    /* BT.601 chroma. The exact matrix does not matter (any consistent one
       separates hue from brightness); 601 is what every keyer in the field
       quotes, so tolerance numbers here mean the same thing as elsewhere. */
    function cb(r, g, b) { return -0.168736 * r - 0.331264 * g + 0.5 * b; }
    function cr(r, g, b) { return 0.5 * r - 0.418688 * g - 0.081312 * b; }
    function luma(r, g, b) { return 0.299 * r + 0.587 * g + 0.114 * b; }

    function defaults() {
        return { mode: 'color', color: [0, 177, 64], tolerance: 32, softness: 22, spill: 60 };
    }

    /* ── automatic key detection ──────────────────────────────────────────── */

    /**
     * Guess what the background is by looking at the FRAME BORDER.
     *
     * The border is the honest sample: whatever the subject is, it is not
     * usually flush against all four edges, so the ring of outermost pixels is
     * background far more often than not. Centre-sampling would pick the
     * subject on any close-up — the exact failure that makes "auto" feel
     * broken. Corners alone are worse still: a vignette darkens them and the
     * detector reports a black key on a green screen.
     *
     * Two refinements, both from a 33-frame ground-truth bench (2026-09-02,
     * tests/scratch/vbr-autodetect-scratch.mjs; F1 0.79 → 0.92 at the same
     * 0.45 threshold):
     *
     *   • LUMA-NORMALISED chroma spread. A green screen lit from one side, or
     *     with a shadow band, is one hue at many brightnesses; raw CbCr spread
     *     read that as "not flat" (gradient-lit green: confidence 0.24, shadow
     *     band: 0.12 — both misses). Scaling each sample's chroma by
     *     meanLuma/luma before measuring spread makes brightness free.
     *   • SEGMENTED border. A talking head centred at the bottom of the frame
     *     touches the border, and its skin pulled the mean off the backdrop
     *     (confidence 0.00 — a miss on the single most common real chroma-key
     *     shot). The ring is cut into 32 runs; the largest chroma-cluster of
     *     runs is the backdrop, and it must cover at least HALF the ring or we
     *     say nothing: without that gate a sunset gradient's dominant run
     *     scored 0.94.
     *
     * Returns a confidence so the UI can stay quiet when it does not know:
     * an ordinary room has no key colour, and offering one there is worse than
     * offering nothing.
     */
    var SEGMENTS_PER_EDGE = 8;      // 32 runs around the ring
    var CLUSTER_RADIUS = 10;        // CbCr distance that keeps two runs together
    var MIN_COVERAGE = 0.5;         // the backdrop must own half the ring

    function ringStats(samples) {
        var n = samples.length, sr = 0, sg = 0, sb = 0, i, p;
        for (i = 0; i < n; i++) { sr += samples[i][0]; sg += samples[i][1]; sb += samples[i][2]; }
        var mr = sr / n, mg = sg / n, mb = sb / n;
        var ml = luma(mr, mg, mb);
        var mcb = cb(mr, mg, mb), mcr = cr(mr, mg, mb);
        var spread = 0, lspread = 0;
        for (i = 0; i < n; i++) {
            p = samples[i];
            var L = luma(p[0], p[1], p[2]);
            var k = ml / Math.max(L, 24);                 // brightness-free chroma
            var dcb = cb(p[0], p[1], p[2]) * k - mcb, dcr = cr(p[0], p[1], p[2]) * k - mcr;
            spread += Math.sqrt(dcb * dcb + dcr * dcr);
            lspread += Math.abs(L - ml);
        }
        spread /= n; lspread /= n;
        return {
            mr: mr, mg: mg, mb: mb, ml: ml, mcb: mcb, mcr: mcr, n: n,
            tight: Math.max(0, 1 - spread / 26),          // 1 = flat backdrop
            flatL: Math.max(0, 1 - lspread / 30),
            sat: Math.sqrt(mcb * mcb + mcr * mcr),        // 0 = grey
        };
    }

    function verdict(st, scale) {
        if (st.ml < 42 && st.flatL > 0.55) return { mode: 'dark',  color: [0, 0, 0],       confidence: Math.min(1, st.flatL) * scale };
        if (st.ml > 212 && st.flatL > 0.55) return { mode: 'light', color: [255, 255, 255], confidence: Math.min(1, st.flatL) * scale };
        // A chromatic key needs BOTH saturation and tightness. Either alone is
        // an ordinary scene: a sunset is saturated and not flat, a grey wall is
        // flat and not saturated.
        return {
            mode: 'color',
            color: [Math.round(st.mr), Math.round(st.mg), Math.round(st.mb)],
            confidence: Math.min(1, st.sat / 45) * st.tight * scale,
        };
    }

    function autoDetect(canvas) {
        var W = canvas.width, H = canvas.height;
        var none = { mode: 'color', color: [0, 177, 64], confidence: 0 };
        if (!W || !H) return none;

        var ctx = canvas.getContext('2d', { willReadFrequently: true });
        var band = Math.max(2, Math.round(Math.min(W, H) * 0.06));
        var runs = [];

        function collect(x, y, w, h, horizontal) {
            w = Math.max(1, Math.min(w, W - x));
            h = Math.max(1, Math.min(h, H - y));
            var d = ctx.getImageData(x, y, w, h).data;
            // Stride so a 4K border does not cost more than a 480p one.
            var step = Math.max(1, Math.floor((w * h) / 4000));
            var buckets = [];
            for (var s = 0; s < SEGMENTS_PER_EDGE; s++) buckets.push([]);
            for (var i = 0, px = 0; i < d.length; i += 4, px++) {
                if (px % step) continue;
                var lx = px % w, ly = (px / w) | 0;
                var seg = Math.min(SEGMENTS_PER_EDGE - 1,
                    Math.floor((horizontal ? lx / w : ly / h) * SEGMENTS_PER_EDGE));
                buckets[seg].push([d[i], d[i + 1], d[i + 2]]);
            }
            for (var b = 0; b < buckets.length; b++) if (buckets[b].length) runs.push(buckets[b]);
        }
        collect(0, 0, W, band, true);            // top
        collect(0, H - band, W, band, true);     // bottom
        collect(0, 0, band, H, false);           // left
        collect(W - band, 0, band, H, false);    // right

        var total = 0, i, j;
        for (i = 0; i < runs.length; i++) total += runs[i].length;
        if (!total) return none;

        // Per-run chroma, then the largest cluster of runs within CLUSTER_RADIUS
        // of one another — the backdrop, if there is one.
        var means = runs.map(function (r) { var t = ringStats(r); return { cb: t.mcb, cr: t.mcr, l: t.ml, n: r.length }; });
        var bestCount = -1, bestIdx = [];
        for (i = 0; i < means.length; i++) {
            var idx = [], count = 0;
            for (j = 0; j < means.length; j++) {
                var a = means[i], b = means[j];
                var k = a.l / Math.max(b.l, 24);
                var dcb = b.cb * k - a.cb, dcr = b.cr * k - a.cr;
                if (Math.sqrt(dcb * dcb + dcr * dcr) < CLUSTER_RADIUS) { idx.push(j); count += b.n; }
            }
            if (count > bestCount) { bestCount = count; bestIdx = idx; }
        }
        var coverage = bestCount / total;
        var cluster = [];
        for (i = 0; i < bestIdx.length; i++) cluster = cluster.concat(runs[bestIdx[i]]);

        var out = verdict(ringStats(cluster), coverage >= MIN_COVERAGE ? 1 : 0);
        out.coverage = coverage;
        return out;
    }

    /**
     * Read the colour under a normalised (0..1) point — the eyedropper.
     * Averages a small neighbourhood: a single pixel lands on compression noise
     * often enough that "pick the screen" would sometimes key nothing.
     */
    function sampleAt(canvas, nx, ny) {
        var W = canvas.width, H = canvas.height;
        var x = Math.max(0, Math.min(W - 1, Math.round(nx * W)));
        var y = Math.max(0, Math.min(H - 1, Math.round(ny * H)));
        var r = Math.max(1, Math.round(Math.min(W, H) * 0.006));
        var x0 = Math.max(0, x - r), y0 = Math.max(0, y - r);
        var w = Math.min(r * 2 + 1, W - x0), h = Math.min(r * 2 + 1, H - y0);
        var d = canvas.getContext('2d', { willReadFrequently: true }).getImageData(x0, y0, w, h).data;
        var sr = 0, sg = 0, sb = 0, n = 0;
        for (var i = 0; i < d.length; i += 4) { sr += d[i]; sg += d[i + 1]; sb += d[i + 2]; n++; }
        return [Math.round(sr / n), Math.round(sg / n), Math.round(sb / n)];
    }

    /* ── the key itself ───────────────────────────────────────────────────── */

    var work = null, workCtx = null;
    function scratch(w, h) {
        if (!work) {
            work = document.createElement('canvas');
            workCtx = work.getContext('2d', { willReadFrequently: true });
        }
        if (work.width !== w || work.height !== h) { work.width = w; work.height = h; }
        return work;
    }

    function matte(src, opts) {
        opts = Object.assign(defaults(), opts || {});
        var W = src.width, H = src.height;
        scratch(W, H);
        workCtx.clearRect(0, 0, W, H);
        workCtx.drawImage(src, 0, 0);
        var img = workCtx.getImageData(0, 0, W, H);
        var d = img.data;

        var out = document.createElement('canvas');
        out.width = W; out.height = H;

        // tolerance → where alpha reaches 0 (fully background)
        // softness   → the width of the ramp from background to subject
        var mode = opts.mode;
        var spill = Math.max(0, Math.min(100, opts.spill)) / 100;

        var i, r, g, b, dist, a, other;

        if (mode === 'dark' || mode === 'light') {
            // Luma key. Tolerance maps onto the 0..255 luma axis directly, which
            // is what "everything darker than this is background" means.
            // Symmetric by construction: 'dark' measures brightness away from
            // black, 'light' measures darkness away from white, and both then
            // read as "further from the backdrop than `tolerance` ⇒ subject".
            var t = (opts.tolerance / 100) * 255;
            var s = Math.max(1, (opts.softness / 100) * 255);
            var dark = mode === 'dark';
            for (i = 0; i < d.length; i += 4) {
                var L = luma(d[i], d[i + 1], d[i + 2]);
                a = ((dark ? L : 255 - L) - t) / s;
                d[i + 3] = a <= 0 ? 0 : (a >= 1 ? 255 : (a * 255) | 0);
            }
        } else {
            var kr = opts.color[0], kg = opts.color[1], kb = opts.color[2];
            var kcb = cb(kr, kg, kb), kcr = cr(kr, kg, kb);
            // The chroma plane spans about ±128; a tolerance of 100 must be able
            // to key an entire hemisphere of it, so scale to 90.
            var tol = (opts.tolerance / 100) * 90;
            var soft = Math.max(1, (opts.softness / 100) * 90);

            // Which channel the backdrop dominates decides where spill goes.
            var keyCh = (kg >= kr && kg >= kb) ? 1 : ((kb >= kr) ? 2 : 0);

            for (i = 0; i < d.length; i += 4) {
                r = d[i]; g = d[i + 1]; b = d[i + 2];
                var dcb = cb(r, g, b) - kcb, dcr = cr(r, g, b) - kcr;
                dist = Math.sqrt(dcb * dcb + dcr * dcr);
                a = (dist - tol) / soft;
                a = a <= 0 ? 0 : (a >= 1 ? 1 : a);
                d[i + 3] = (a * 255) | 0;

                // Despill on everything the key touched at all — including the
                // fully-keyed pixels, because a soft edge blends them back in.
                if (spill > 0 && a < 1) {
                    if (keyCh === 1) {
                        other = (r + b) * 0.5;
                        if (g > other) d[i + 1] = g + (other - g) * spill;
                    } else if (keyCh === 2) {
                        other = (r + g) * 0.5;
                        if (b > other) d[i + 2] = b + (other - b) * spill;
                    } else {
                        other = (g + b) * 0.5;
                        if (r > other) d[i] = r + (other - r) * spill;
                    }
                }
            }
        }

        out.getContext('2d').putImageData(img, 0, 0);
        return out;
    }

    root.VbrChroma = {
        autoDetect: autoDetect,
        sampleAt: sampleAt,
        matte: matte,
        defaults: defaults,
    };
})(typeof window !== 'undefined' ? window : globalThis);
